//core
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ActionReducerMapBuilder } from "@reduxjs/toolkit/src/mapBuilders";
import { NoInfer } from "@reduxjs/toolkit/src/tsHelpers";
import { aiCopilotService } from "../../../services/common/aiCopilot.service";
import { getDraftErrorReason } from "../../../helpers/getDraftErrorReason";
import { setUser } from "../user";
import { setGeneralProgressHide, setGeneralProgressShow, setGeneralSnackbarState } from "../ui";

export type DealDraftCondition = Record<string, unknown>;

export type ReviewPendingResult = {
  status: 'pending';
  expiresAt: string;
  condition: DealDraftCondition;
};

export type ReviewAppliedResult = {
  status: 'applied';
  dealId: string | number;
  url?: string;
};

export type ReviewResult = ReviewPendingResult | ReviewAppliedResult;

export type DraftErrorReason =
  | 'expired' // 410
  | 'superseded' // 409
  | 'forbidden' // 403
  | 'invalid' // 400
  | 'unavailable';

export type payloadApplyDealDraft = {
  draftId: string;
  condition?: DealDraftCondition;
};

export type AiCopilotSliceState = {
  isOpen: boolean;
  lastErrorCode: string | null;
  dealDraft: {
    data: ReviewResult | null,
    error: any,
    isFetching: boolean,
  },
  applyDealDraft: {
    data: ReviewAppliedResult | null,
    error: any,
    isFetching: boolean,
  },
};

const initialState: AiCopilotSliceState = {
  isOpen: false,
  lastErrorCode: null,
  dealDraft: {
    data: null,
    error: null,
    isFetching: false,
  },
  applyDealDraft: {
    data: null,
    error: null,
    isFetching: false,
  },
};

export const getDealDraftData: any = createAsyncThunk(
  'aiCopilot/getDealDraftData',
  async (draftId: string, { rejectWithValue, dispatch, signal }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await aiCopilotService.getDealDraft(draftId, signal);
      const data = await response.json().catch(() => ({}));

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setGeneralProgressHide());
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue({ ...data, reason: getDraftErrorReason(response.status) });
      }

      dispatch(setGeneralProgressHide());
      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const applyDealDraftData: any = createAsyncThunk(
  'aiCopilot/applyDealDraftData',
  async (payload: payloadApplyDealDraft, { rejectWithValue, dispatch }) => {
    try {
      const response = await aiCopilotService.applyDealDraft(payload.draftId, payload.condition);
      const data = await response.json().catch(() => ({}));

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        return rejectWithValue({ ...data, reason: getDraftErrorReason(response.status) });
      }

      dispatch(
        setGeneralSnackbarState({
          open: true,
          type: 'success',
          message: 'success',
          messageKey: 'common.messages.success',
        })
      );

      return data;
    } catch (error) {
      return rejectWithValue(error)
    }
  }
);

const aiCopilotSlice = createSlice({
  name: 'aiCopilot',
  initialState,
  reducers: {
    openAiCopilot(state: AiCopilotSliceState) {
      state.isOpen = true;
    },
    closeAiCopilot(state: AiCopilotSliceState) {
      state.isOpen = false;
    },
    setAiCopilotError(state: AiCopilotSliceState, action: PayloadAction<string | null>) {
      state.lastErrorCode = action.payload;
    },
    clearAiCopilotState() {
      return initialState;
    },
    clearDealDraftState(state: AiCopilotSliceState) {
      state.dealDraft.error = null;
      state.dealDraft.isFetching = false;
      state.dealDraft.data = null;
    },
    clearApplyDealDraftState(state: AiCopilotSliceState) {
      state.applyDealDraft.error = null;
      state.applyDealDraft.isFetching = false;
      state.applyDealDraft.data = null;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<NoInfer<AiCopilotSliceState>>) => {
    builder.addCase(getDealDraftData.pending, (state) => {
      state.dealDraft.isFetching = true;
      state.dealDraft.error = null;
    });
    builder.addCase(getDealDraftData.fulfilled, (state, action) => {
      state.dealDraft.error = null;
      state.dealDraft.isFetching = false;
      state.dealDraft.data = action.payload;
    });
    builder.addCase(getDealDraftData.rejected, (state, action) => {
      state.dealDraft.isFetching = false;
      state.dealDraft.error = action.payload;
      state.dealDraft.data = null;
    });

    builder.addCase(applyDealDraftData.pending, (state) => {
      state.applyDealDraft.isFetching = true;
      state.applyDealDraft.error = null;
    });
    builder.addCase(applyDealDraftData.fulfilled, (state, action) => {
      state.applyDealDraft.error = null;
      state.applyDealDraft.isFetching = false;
      state.applyDealDraft.data = action.payload;
    });
    builder.addCase(applyDealDraftData.rejected, (state, action) => {
      state.applyDealDraft.isFetching = false;
      state.applyDealDraft.error = action.payload;
      state.applyDealDraft.data = null;
    });
  },
});

export default aiCopilotSlice.reducer;

export const {
  openAiCopilot,
  closeAiCopilot,
  setAiCopilotError,
  clearAiCopilotState,
  clearDealDraftState,
  clearApplyDealDraftState,
} = aiCopilotSlice.actions;
